import { useEffect, useRef, useState } from "react";
import { formatDate } from "../../../api/dates";
import type { ProgressSite, ProgressStage, ReportRow } from "../../../api/routing";
import { Segmented } from "../../../components/ui";
import { loadLeaflet, STREET_TILES, type LLayerGroup, type LMap, type Leaflet } from "../leaflet";
import { SERIES, seriesOf, type Series } from "./summary";

/** Where the visits were, coloured by what became of them; or every site,
 * coloured by how far its work has got, hollow until it has been scoped. */

export type MapLayer = "visits" | "sites";

const LAYERS: readonly { value: MapLayer; label: string }[] = [
  { value: "visits", label: "Visits in the period" },
  { value: "sites", label: "All sites by stage" },
];

/** Marker colours. Pending is a darker grey than on the charts so it still
 * shows against the street map. */
const OUTCOME_COLOUR: Record<Series, string> = {
  done: "#067b39",
  not_done: "#e8a33d",
  overdue: "#b23b3b",
  pending: "#7d8774",
};

/** A site name is data from an upload; a popup is HTML. */
function escapeHtml(text: string): string {
  return text.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c] ?? c);
}

function visitPopup(row: ReportRow): string {
  const outcome = SERIES.find((s) => s.key === seriesOf(row))?.label ?? row.outcome;
  const why = [row.reason_label, row.note].filter(Boolean).join(" — ");
  return [
    `<b>${escapeHtml(row.site_name)}</b>`,
    row.work_order ? `<div>${escapeHtml(row.work_order)}</div>` : "",
    `<div>${escapeHtml(formatDate(row.date))} · ${escapeHtml(row.crew)}</div>`,
    `<div><b>${escapeHtml(outcome)}</b>${why ? `: ${escapeHtml(why)}` : ""}</div>`,
  ].join("");
}

function sitePopup(site: ProgressSite, stage: string): string {
  return [
    `<b>${escapeHtml(site.name)}</b>`,
    site.work_order ? `<div>${escapeHtml(site.work_order)}</div>` : "",
    `<div>${escapeHtml(stage)}</div>`,
    `<div>${site.scoped ? "Scoped: QCP on file" : "Not scoped yet"}</div>`,
  ].join("");
}

export function VisitsMap({
  rows,
  sites,
  stages,
  initialLayer = "visits",
}: {
  rows: readonly ReportRow[];
  sites: readonly ProgressSite[];
  stages: readonly ProgressStage[];
  initialLayer?: MapLayer;
}) {
  const box = useRef<HTMLDivElement>(null);
  const mapRef = useRef<{ L: Leaflet; map: LMap; markers: LLayerGroup } | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [layer, setLayer] = useState<MapLayer>(initialLayer);

  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || box.current === null || mapRef.current !== null) {
          return;
        }
        const map = L.map(box.current, { zoomControl: true }).setView([-34.0, 18.45], 10);
        L.tileLayer(STREET_TILES, { maxZoom: 19, attribution: "© OpenStreetMap contributors" }).addTo(map);
        mapRef.current = { L, map, markers: L.layerGroup().addTo(map) };
        setReady(true);
      })
      .catch(() => setFailed(true));
    return () => {
      cancelled = true;
      mapRef.current?.map.remove();
      mapRef.current = null;
      setReady(false);
    };
  }, []);

  useEffect(() => {
    const current = mapRef.current;
    if (!ready || current === null) {
      return;
    }
    const { L, map, markers } = current;
    markers.clearLayers();
    const points: [number, number][] = [];
    if (layer === "visits") {
      for (const row of rows) {
        if (row.lat === null || row.lon === null) {
          continue;
        }
        const at: [number, number] = [row.lat, row.lon];
        points.push(at);
        L.circleMarker(at, { radius: 7, color: "#ffffff", weight: 2, fillColor: OUTCOME_COLOUR[seriesOf(row)], fillOpacity: 0.95 })
          .bindPopup(visitPopup(row))
          .addTo(markers);
      }
    } else {
      const label = new Map(stages.map((stage) => [stage.status, stage]));
      for (const site of sites) {
        const at: [number, number] = [site.lat, site.lon];
        const stage = label.get(site.status);
        const colour = stage?.color ?? "#7d8774";
        points.push(at);
        L.circleMarker(at, { radius: 6, color: colour, weight: 2, fillColor: colour, fillOpacity: site.scoped ? 0.85 : 0.1 })
          .bindPopup(sitePopup(site, stage?.label ?? site.status))
          .addTo(markers);
      }
    }
    if (points.length === 1) {
      map.setView(points[0], 15);
    } else if (points.length > 1) {
      map.fitBounds(L.latLngBounds(points).pad(0.1));
    }
  }, [ready, layer, rows, sites, stages]);

  const unplaced = layer === "visits" ? rows.filter((row) => row.lat === null || row.lon === null).length : 0;
  return (
    <div className="vd-map-card">
      <Segmented label="Map shows" options={LAYERS} value={layer} onChange={setLayer} />
      {failed ? <p className="notice warn">The map could not be loaded. The lists below still hold every visit.</p> : null}
      <div ref={box} className="vd-map" role="region" aria-label="Map of the visits" />
      {layer === "visits" ? (
        <ul className="vd-legend" aria-label="Map key">
          {SERIES.map((s) => (
            <li key={s.key}>
              <span className="vd-dot" style={{ background: OUTCOME_COLOUR[s.key] }} aria-hidden="true" />
              {s.label}
            </li>
          ))}
          {unplaced > 0 ? <li className="muted">{unplaced} visit{unplaced === 1 ? " has" : "s have"} no position and are not drawn</li> : null}
        </ul>
      ) : (
        <ul className="vd-legend" aria-label="Map key">
          {stages
            .filter((stage) => stage.count > 0)
            .map((stage) => (
              <li key={stage.status}>
                <span className="vd-dot" style={{ background: stage.color }} aria-hidden="true" />
                {stage.label}
              </li>
            ))}
          <li>
            <span className="vd-dot vd-dot-hollow" aria-hidden="true" />
            Hollow: not scoped yet
          </li>
        </ul>
      )}
    </div>
  );
}
